import { defineStore } from 'pinia';
import { ref } from 'vue';
import { GlobalSettingsEnum, SimpleGlobalSettingDto } from '@/api';
import { GlobalSetting as GlobalSettingConstants } from '@/constants/GlobalSettting.ts';
import globalSettingService from '@/services/api/globalSettingService.ts';

const globalSettingFactory = (): SimpleGlobalSettingDto =>
  ({
    id: '',
    name: '',
    value: 0,
  }) as SimpleGlobalSettingDto;

export const GLOBAL_SETTING_MIN_TICKET_DAYS_IN_ADVANCE_VALUE = 1;
export const GLOBAL_SETTING_MAX_TICKET_DAYS_IN_ADVANCE_VALUE = 10;
export const GLOBAL_SETTING_MIN_CARD_DAYS_IN_ADVANCE_VALUE = 30;
export const GLOBAL_SETTING_MAX_CARD_DAYS_IN_ADVANCE_VALUE = 90;
export const GLOBAL_SETTING_CUSTOMER_NOTIFICATION_MIN_CUSTOMER_PROFILE_DAYS_IN_ADVANCE_VALUE = 30;
export const GLOBAL_SETTING_CUSTOMER_NOTIFICATION_MAX_CUSTOMER_PROFILE_DAYS_IN_ADVANCE_VALUE = 90;

export const GLOBAL_SETTING_MAX_DAYS_BETWEEN_CURRENT_DATE_AND_TICKET_FARE_VALID_FROM_BEFORE_START_AT_ESHOP = 90;
/**
 * Fallback použitý, když setting v systému chybí. 0 = bez omezení - shodně s backendem,
 * který při chybějícím / nekladném settingu minimální platnost pro storno vůbec nevynucuje.
 */
export const MIN_PREPAID_TICKET_VALIDITY_FOR_CANCELLATION_IN_DAYS = 0;

export const useGlobalSettingStore = defineStore('globalSetting', () => {
  const globalSettings = ref<Array<SimpleGlobalSettingDto>>([]);

  // DTO refs – plný objekt SimpleGlobalSettingDto
  const customerCardValidity = ref<SimpleGlobalSettingDto>(globalSettingFactory());
  const ticketReturnFeePercentage = ref<SimpleGlobalSettingDto>(globalSettingFactory());
  const customerProfileValidToInYears = ref<SimpleGlobalSettingDto>(globalSettingFactory());

  // Numerické refs – pouze hodnota s defaultem
  const customerNotificationMinCardDaysInAdvance = ref(GLOBAL_SETTING_MIN_CARD_DAYS_IN_ADVANCE_VALUE);
  const customerNotificationMaxCardDaysInAdvance = ref(GLOBAL_SETTING_MAX_CARD_DAYS_IN_ADVANCE_VALUE);
  const customerNotificationMinTicketDaysInAdvance = ref(GLOBAL_SETTING_MIN_TICKET_DAYS_IN_ADVANCE_VALUE);
  const customerNotificationMaxTicketDaysInAdvance = ref(GLOBAL_SETTING_MAX_TICKET_DAYS_IN_ADVANCE_VALUE);
  const customerNotificationMinCustomerProfileDaysInAdvance = ref(
    GLOBAL_SETTING_CUSTOMER_NOTIFICATION_MIN_CUSTOMER_PROFILE_DAYS_IN_ADVANCE_VALUE,
  );
  const customerNotificationMaxCustomerProfileDaysInAdvance = ref(
    GLOBAL_SETTING_CUSTOMER_NOTIFICATION_MAX_CUSTOMER_PROFILE_DAYS_IN_ADVANCE_VALUE,
  );
  const maxDaysBetweenCurrentDateAndTicketFareValidFromBeforeStartAtEshop = ref(
    GLOBAL_SETTING_MAX_DAYS_BETWEEN_CURRENT_DATE_AND_TICKET_FARE_VALID_FROM_BEFORE_START_AT_ESHOP,
  );
  const minPrepaidTicketValidityForCancellationInDays = ref(MIN_PREPAID_TICKET_VALIDITY_FOR_CANCELLATION_IN_DAYS);

  // Deklarativní mapování: enum klíč -> ref + defaultní hodnota.
  const dtoBindings: Array<[string, typeof customerCardValidity]> = [
    [GlobalSettingConstants.CUSTOMER_CARD_VALIDITY, customerCardValidity],
    [GlobalSettingsEnum.TICKET_FARE_RETURN_FEE, ticketReturnFeePercentage],
    [GlobalSettingsEnum.CUSTOMER_PROFILE_DEFAULT_VALID_TO_IN_YEARS, customerProfileValidToInYears],
  ];

  const numericBindings: Array<[string, typeof customerNotificationMinCardDaysInAdvance, number]> = [
    [
      GlobalSettingsEnum.CUSTOMER_NOTIFICATION_MIN_CARD_DAYS_IN_ADVANCE,
      customerNotificationMinCardDaysInAdvance,
      GLOBAL_SETTING_MIN_CARD_DAYS_IN_ADVANCE_VALUE,
    ],
    [
      GlobalSettingsEnum.CUSTOMER_NOTIFICATION_MAX_CARD_DAYS_IN_ADVANCE,
      customerNotificationMaxCardDaysInAdvance,
      GLOBAL_SETTING_MAX_CARD_DAYS_IN_ADVANCE_VALUE,
    ],
    [
      GlobalSettingsEnum.CUSTOMER_NOTIFICATION_MIN_TICKET_DAYS_IN_ADVANCE,
      customerNotificationMinTicketDaysInAdvance,
      GLOBAL_SETTING_MIN_TICKET_DAYS_IN_ADVANCE_VALUE,
    ],
    [
      GlobalSettingsEnum.CUSTOMER_NOTIFICATION_MAX_TICKET_DAYS_IN_ADVANCE,
      customerNotificationMaxTicketDaysInAdvance,
      GLOBAL_SETTING_MAX_TICKET_DAYS_IN_ADVANCE_VALUE,
    ],
    [
      GlobalSettingsEnum.CUSTOMER_NOTIFICATION_MIN_CUSTOMER_PROFILE_DAYS_IN_ADVANCE,
      customerNotificationMinCustomerProfileDaysInAdvance,
      GLOBAL_SETTING_CUSTOMER_NOTIFICATION_MIN_CUSTOMER_PROFILE_DAYS_IN_ADVANCE_VALUE,
    ],
    [
      GlobalSettingsEnum.CUSTOMER_NOTIFICATION_MAX_CUSTOMER_PROFILE_DAYS_IN_ADVANCE,
      customerNotificationMaxCustomerProfileDaysInAdvance,
      GLOBAL_SETTING_CUSTOMER_NOTIFICATION_MAX_CUSTOMER_PROFILE_DAYS_IN_ADVANCE_VALUE,
    ],
    [
      GlobalSettingsEnum.MAX_DAYS_BETWEEN_CURRENT_DATE_AND_TICKET_FARE_VALID_FROM_BEFORE_START_AT_ESHOP,
      maxDaysBetweenCurrentDateAndTicketFareValidFromBeforeStartAtEshop,
      GLOBAL_SETTING_MAX_DAYS_BETWEEN_CURRENT_DATE_AND_TICKET_FARE_VALID_FROM_BEFORE_START_AT_ESHOP,
    ],
    [
      GlobalSettingsEnum.MIN_PREPAID_TICKET_VALIDITY_FOR_CANCELLATION_IN_DAYS,
      minPrepaidTicketValidityForCancellationInDays,
      MIN_PREPAID_TICKET_VALIDITY_FOR_CANCELLATION_IN_DAYS,
    ],
  ];

  const setGlobalSettings = (payload: Array<SimpleGlobalSettingDto>) => {
    globalSettings.value = payload;

    // Jediný průchod -> O(n) místo opakovaných payload.find().
    const settingsByName = new Map<string, SimpleGlobalSettingDto>();
    for (const setting of payload) {
      settingsByName.set(setting.name, setting);
    }

    for (const [name, targetRef] of dtoBindings) {
      targetRef.value = settingsByName.get(name) ?? globalSettingFactory();
    }

    for (const [name, targetRef, defaultValue] of numericBindings) {
      targetRef.value = settingsByName.get(name)?.value ?? defaultValue;
    }
  };

  const getGlobalSettings = async (): Promise<SimpleGlobalSettingDto[]> => {
    if (globalSettings.value.length > 0) {
      return globalSettings.value;
    }
    const settings = await globalSettingService.getGlobalSetting();
    setGlobalSettings(settings);
    return globalSettings.value;
  };

  return {
    globalSettings,
    customerCardValidity,
    ticketReturnFeePercentage,
    customerProfileValidToInYears,
    customerNotificationMinCardDaysInAdvance,
    customerNotificationMaxCardDaysInAdvance,
    customerNotificationMinTicketDaysInAdvance,
    customerNotificationMaxTicketDaysInAdvance,
    customerNotificationMinCustomerProfileDaysInAdvance,
    customerNotificationMaxCustomerProfileDaysInAdvance,
    maxDaysBetweenCurrentDateAndTicketFareValidFromBeforeStartAtEshop,
    minPrepaidTicketValidityForCancellationInDays,

    getGlobalSettings,
    setGlobalSettings,
  };
});
